import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { FileText, Search, Loader2, Hash } from "lucide-react";
import { cn } from "@/lib/utils";

interface Lead {
  id: string;
  title: string;
  client_name: string | null;
  status: string | null;
}

interface LeadMentionPopoverProps {
  onSelectLead: (lead: Lead) => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const LeadMentionPopover = ({
  onSelectLead,
  trigger,
  open: controlledOpen,
  onOpenChange,
}: LeadMentionPopoverProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads-for-mention", searchTerm],
    queryFn: async () => {
      let query = supabase
        .from("leads")
        .select("id, title, client_name, status")
        .order("updated_at", { ascending: false })
        .limit(20);

      if (searchTerm) {
        query = query.or(`title.ilike.%${searchTerm}%,client_name.ilike.%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as Lead[];
    },
    enabled: isOpen,
  });

  const handleSelect = (lead: Lead) => {
    onSelectLead(lead);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getStatusColor = (status: string | null) => {
    switch (status) {
      case "lead":
      case "quote_generated":
      case "quote_sent":
        return "bg-blue-500/20 text-blue-600";
      case "quote_accepted":
      case "confirmed":
        return "bg-amber-500/20 text-amber-600";
      case "executing":
      case "invoicing":
        return "bg-orange-500/20 text-orange-600";
      case "completed":
        return "bg-green-500/20 text-green-600";
      case "cancelled":
      case "aborted":
        return "bg-red-500/20 text-red-600";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8" type="button">
            <Hash className="h-4 w-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9"
              autoFocus
            />
          </div>
        </div>
        <ScrollArea className="h-[250px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : leads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <FileText className="h-8 w-8 text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                {searchTerm ? "No leads found" : "No leads available"}
              </p>
            </div>
          ) : (
            <div className="py-1">
              {leads.map((lead) => (
                <button
                  key={lead.id}
                  className="w-full flex items-start gap-3 px-3 py-2 hover:bg-accent text-left"
                  onClick={() => handleSelect(lead)}
                  type="button"
                >
                  <FileText className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{lead.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {lead.client_name && (
                        <span className="text-xs text-muted-foreground truncate">
                          {lead.client_name}
                        </span>
                      )}
                      {lead.status && (
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded capitalize",
                          getStatusColor(lead.status)
                        )}>
                          {lead.status.replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
